import React, { FC, useEffect, useState } from 'react'
import Typography from '../typography'
import { StyledBanner } from './style'

//* utils
import { pageLocale } from '../../../../preview/utils/localization'
import { URLS } from '../../../constants/environment.constants'
import { LocaleCodes, LocaleCodesSlugPrefix } from '../../../constants/localization.constants'
import { delocalizeUrl } from '../../../utils/functions.utils'
import { correctSlugPrefix } from '../../../utils/link.utils'
import { findTarget } from '../../../utils/microcopy.utils'
import localizedBannerStaticQuery from './localizedBannerStaticQuery'

type Props = {
  slug: string
  defaultBrowserLanguage: string
  browserLanguageData: any
  availableLocales: any
}

const LocalizedBanner: FC<Props> = ({
  slug,
  defaultBrowserLanguage,
  browserLanguageData,
  availableLocales,
}) => {
  const [localizedPageExist, setLocalizedPageExist] = useState(false)
  const [localizedUrl, setLocalizedUrl] = useState('')
  const [displayBanner, setDisplayBanner] = useState(true)

  const localeCode = LocaleCodesSlugPrefix[defaultBrowserLanguage]
  const localeInfo: any = slug ? pageLocale(slug) : null
  const code = localeInfo?.code || LocaleCodes.ENGLISH.code

  //* microcopy
  const queryData: any = localizedBannerStaticQuery?.contentfulLocalizedBannerMicroCopy() || {}
  const microcopySet = browserLanguageData?.contentfulMicrocopySet || null
  const bannerMicroCopySet = microcopySet || queryData?.contentfulMicrocopySet
  if (!bannerMicroCopySet) return null

  let bannerMicrocopy = localeCode
    ? findTarget(localeCode.toUpperCase(), bannerMicroCopySet.values, false)
    : null

  const [bannerTextFirstPart, bannerTextSecondPart] = bannerMicrocopy
    ? bannerMicrocopy.split('. ')
    : ''

  const setLocalStorageWithExpiration = (key: string, value: string, ttl_days: number) => {
    if (typeof window === 'undefined') return null
    const now = new Date()
    const item = {
      value: value,
      expiration: now.getTime() + ttl_days * 24 * 60 * 60 * 1000,
    }

    localStorage.setItem(key, JSON.stringify(item))
  }

  const getLocalStorageWithExpiration = (key: string) => {
    if (typeof window === 'undefined') return null
    const itemStr = localStorage.getItem(key)
    if (!itemStr) return null

    const item = JSON.parse(itemStr)
    const now = new Date()

    //* compare the expiration time of the item with the current time
    if (now.getTime() > item.expiration) {
      //* If the item is expired, delete the item from storage and return null
      localStorage.removeItem(key)
      return null
    }

    return item.value
  }

  //* Check if the localized page exists for users default browser language in the available locales {}
  useEffect(() => {
    const previouslyDeclinedBanner = getLocalStorageWithExpiration(`displayBanner`)
    if (
      slug &&
      defaultBrowserLanguage &&
      localeCode &&
      !previouslyDeclinedBanner &&
      availableLocales?.[defaultBrowserLanguage] &&
      code !== defaultBrowserLanguage
    ) {
      setLocalizedPageExist(true)

      const delocalizedSlug = delocalizeUrl(slug)
      const originURL = typeof window !== 'undefined' ? window.location.origin : URLS.PROD
      const localeCodeSlug = localeCode === 'en' ? '' : `/${localeCode}`
      const destinationUrl = originURL + localeCodeSlug + correctSlugPrefix(delocalizedSlug)

      setLocalizedUrl(destinationUrl)
    }
  }, [slug, setLocalizedPageExist, setLocalizedUrl, localeCode])

  const handleBannerDisplay = () => {
    setLocalStorageWithExpiration('displayBanner', 'false', 7)
    setDisplayBanner(false)
  }

  if (localizedPageExist && localizedUrl && bannerMicrocopy) {
    return (
      <StyledBanner data-cy="localizedBanner" $displayBanner={displayBanner}>
        <Typography type="bodyXS" className="localizedBanner_text">
          {bannerTextFirstPart}.{' '}
          <span>
            <a href={localizedUrl}>{bannerTextSecondPart}</a>
          </span>
        </Typography>

        <div className="localizedBanner_x-btn" onClick={() => handleBannerDisplay()}>
          ✕
        </div>
      </StyledBanner>
    )
  } else {
    return null
  }
}

export default LocalizedBanner
